import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { PictureTalkSceneViewer } from '../../components/picture-talk/PictureTalkSceneViewer';
import {
  getPictureTalkScene,
  displaySceneTitle,
  displayItemTitle,
  displayItemDescription,
  type PictureTalkSceneDetail,
  type PictureTalkSceneItem,
} from '../../modules/learning/api/pictureTalk';
import { LearningHttpError } from '../../modules/learning/api/stories';
import { ROUTES } from '../../utils/constants';
import { handleSchoolFeatureDisabledIfPresent } from '../../utils/schoolFeatureDisabledStudent';
import { ImageWithFallback } from '../../components/common/ImageWithFallback';
import { usePictureTalkLiveSession } from '../../modules/picture-talk/live/usePictureTalkLiveSession';
import type { PictureTalkLivePhase } from '../../modules/picture-talk/live/usePictureTalkLiveSession';
import {
  PICTURE_TALK_EELAM_DIALECT_KEY,
  PICTURE_TALK_STANDARD_DIALECT_KEY,
} from '../../modules/picture-talk/live/pictureTalkDialectKeys';
import { serviceUrls } from '../../config/serviceUrls';
import { getMySchoolLanguage } from '../../modules/platform/api/schools';

function getTutorHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || '';
  const tenantId = localStorage.getItem('X_TENANT_ID') || '';

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['x-tenant-id'] = tenantId;
  return headers;
}

function phaseLabel(phase: PictureTalkLivePhase): string {
  switch (phase) {
    case 'idle':
      return 'Ready';
    case 'connecting':
      return 'Connecting…';
    case 'connected':
      return 'Live';
    case 'ended':
      return 'Ended';
    case 'error':
      return 'Error';
    default:
      return phase;
  }
}

const PictureTalkScenePage: React.FC = () => {
  const { sceneId: sceneIdParam } = useParams<{ sceneId: string }>();
  const sceneId = sceneIdParam ? decodeURIComponent(sceneIdParam) : '';
  const navigate = useNavigate();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();

  const [scene, setScene] = useState<PictureTalkSceneDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PictureTalkSceneItem | null>(null);

  const heroSignedUrlRefetchDoneRef = useRef(false);

  // Dialect dropdown state for Picture Talk (student-facing)
  const DIALECT_OPTIONS_ENDPOINT = '/v1/tutor/dialect-options';
  const AI_TUTOR_BASE = serviceUrls.gateway;

  const [dialectOptions, setDialectOptions] = useState<{ key: string; name: string }[]>([]);
  const [dialectKey, setDialectKey] = useState<string>(PICTURE_TALK_STANDARD_DIALECT_KEY);

  useEffect(() => {
    const ac = new AbortController();

    function uniqueTrimmedKeys(keys?: string[] | null): string[] {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const raw of keys || []) {
        const key = String(raw || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(key);
      }
      return out;
    }

    function orderDialectOptionsForSchool(
      allOptions: { key: string; name: string }[],
      schoolLanguage?: { preferred_dialect_keys?: string[]; default_dialect_key?: string | null } | null
    ): { key: string; name: string }[] {
      const allByKey = new Map(allOptions.map((d) => [d.key, d]));
      const preferredKeys = uniqueTrimmedKeys(schoolLanguage?.preferred_dialect_keys);
      if (preferredKeys.length === 0) {
        const standard = allByKey.get(PICTURE_TALK_STANDARD_DIALECT_KEY);
        return standard ? [standard] : allOptions.slice(0, 1);
      }
      const ordered = preferredKeys.map((k) => allByKey.get(k)).filter((d): d is { key: string; name: string } => Boolean(d));
      if (ordered.length > 0) return ordered;
      const standard = allByKey.get(PICTURE_TALK_STANDARD_DIALECT_KEY);
      return standard ? [standard] : allOptions.slice(0, 1);
    }

    function resolveStudentDefaultDialectKey(
      visibleOptions: { key: string; name: string }[],
      schoolLanguage?: { preferred_dialect_keys?: string[]; default_dialect_key?: string | null } | null
    ): string {
      const visibleKeys = new Set(visibleOptions.map((d) => d.key));
      const preferredKeys = uniqueTrimmedKeys(schoolLanguage?.preferred_dialect_keys);
      const configuredDefault = String(schoolLanguage?.default_dialect_key || '').trim();
      if (configuredDefault && visibleKeys.has(configuredDefault)) return configuredDefault;
      const firstPreferred = preferredKeys.find((k) => visibleKeys.has(k));
      if (firstPreferred) return firstPreferred;
      if (visibleKeys.has(PICTURE_TALK_STANDARD_DIALECT_KEY)) return PICTURE_TALK_STANDARD_DIALECT_KEY;
      return visibleOptions[0]?.key || PICTURE_TALK_STANDARD_DIALECT_KEY;
    }

    async function loadDialectOptions() {
      try {
        const url = new URL(DIALECT_OPTIONS_ENDPOINT, AI_TUTOR_BASE);
        const dialectReq = fetch(url.toString(), { signal: ac.signal, headers: getTutorHeaders() });
        const schoolReq = getMySchoolLanguage();
        const [dialectSettled, schoolSettled] = await Promise.allSettled([dialectReq, schoolReq]);

        let cleaned: { key: string; name: string }[] = [];
        if (dialectSettled.status === 'fulfilled') {
          try {
            const r = dialectSettled.value;
            if (r.ok) {
              const data = await r.json();
              const raw = Array.isArray(data?.dialects) ? data.dialects : [];
              cleaned = raw
                .filter((x: unknown) => x && typeof x === 'object' && typeof (x as { key?: unknown }).key === 'string')
                .map((x: { key: string; name?: string }) => ({
                  key: String(x.key).trim(),
                  name: String(x.name || x.key).trim() || String(x.key),
                }))
                .filter((x: { key: string; name: string }) => x.key.length > 0);
            } else {
              if (import.meta.env.DEV) console.warn('[PictureTalk] dialect-options request failed', r.status);
            }
          } catch (e) {
            if (import.meta.env.DEV) console.warn('[PictureTalk] dialect-options parse failed', e);
            cleaned = [];
          }
        } else {
          if (import.meta.env.DEV) console.warn('[PictureTalk] dialect-options fetch failed', dialectSettled.reason);
          cleaned = [];
        }

        if (schoolSettled.status === 'fulfilled' && cleaned.length > 0) {
          const schoolLanguage = schoolSettled.value;
          const visibleOptions = orderDialectOptionsForSchool(cleaned, schoolLanguage as any);
          setDialectOptions(visibleOptions);
          setDialectKey(resolveStudentDefaultDialectKey(visibleOptions, schoolLanguage as any));
          if (import.meta.env.DEV) {
            console.log('[PictureTalk] school dialect config applied', {
              schoolLanguage,
              allDialectOptions: cleaned,
              visibleOptions,
              selectedDialectKey: resolveStudentDefaultDialectKey(visibleOptions, schoolLanguage as any),
            });
          }
          return;
        }

        // Fallback to old behavior if school language fails
        if (cleaned.length > 0) {
          setDialectOptions(cleaned);
          setDialectKey((prev) => {
            const keys = new Set(cleaned.map((d) => d.key));
            if (prev && keys.has(prev)) return prev;
            return cleaned[0].key;
          });
        } else {
          setDialectOptions([]);
        }
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') return;
        if (import.meta.env.DEV) console.warn('[PictureTalk] dialect-options could not be loaded', e);
        setDialectOptions([]);
      }
    }

    void loadDialectOptions();
    return () => ac.abort();
  }, [sceneId]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log('[PictureTalk] UI dialect (Start Conversation uses prompt-library resolver)', {
      dialectKey,
      isEelam: dialectKey.trim() === PICTURE_TALK_EELAM_DIALECT_KEY,
    });
  }, [dialectKey]);

  const {
    phase,
    isReady,
    error: liveError,
    startConversation,
    endConversation,
    resetToIdle,
    notifyItemClicked,
  } = usePictureTalkLiveSession({
    navigate,
    refetchFeatureAccess,
    sceneId,
    dialectKey,
  });

  useEffect(() => {
    if (!sceneId) {
      setLoading(false);
      setError('Scene not found');
      setScene(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setLoading(true);
    setSelected(null);
    heroSignedUrlRefetchDoneRef.current = false;
    getPictureTalkScene(sceneId)
      .then((d) => {
        if (!cancelled) {
          setScene(d);
          // Do not auto-select any item on load — selection should remain null until user taps.
        }
      })
      .catch(async (e) => {
        if (cancelled) return;
        if (e instanceof LearningHttpError) {
          if (
            await handleSchoolFeatureDisabledIfPresent({
              status: e.status,
              bodyText: e.bodyText,
              navigate,
              refetchFeatureAccess,
              fallbackFeatureKey: 'picture_talk',
            })
          ) {
            return;
          }
          if (e.status === 404) {
            setError('Scene not found');
            return;
          }
          setError(e.message);
          return;
        }
        setError(e instanceof Error ? e.message : 'Failed to load scene.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sceneId, navigate, refetchFeatureAccess]);

  const title = useMemo(() => (scene ? displaySceneTitle(scene) : 'Picture Talk'), [scene]);

  const imageUrl =
    scene?.primary_image_url?.trim() ||
    scene?.items.find((i) => i.image_url?.trim())?.image_url?.trim() ||
    undefined;

  const panelTitle = selected ? displayItemTitle(selected) : '';
  const panelBody = selected ? displayItemDescription(selected) : '';
  const panelThumb = selected?.image_url?.trim() || undefined;

  const handleHeroPrimaryLoadFailed = useCallback(() => {
    if (!sceneId || heroSignedUrlRefetchDoneRef.current) return;
    heroSignedUrlRefetchDoneRef.current = true;
    void getPictureTalkScene(sceneId)
      .then((d) => setScene(d))
      .catch(() => {
        /* Signed URL may still be bad; viewer shows placeholder. */
      });
  }, [sceneId]);

  const handleItemSelect = useCallback(
    (item: PictureTalkSceneItem) => {
      setSelected(item);
      if (!scene) return;
      if (phase === 'connected' && isReady) {
        notifyItemClicked(item, scene);
      }
    },
    [scene, phase, isReady, notifyItemClicked]
  );

  const showStart =
    phase === 'idle' || phase === 'ended' || phase === 'error';
  const showEnd = phase === 'connecting' || phase === 'connected';

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 pb-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Link
            to={ROUTES.STUDENT_PICTURE_TALK}
            className="inline-flex items-center rounded-lg border-2 border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
          >
            ← காட்சிகளுக்குத் திரும்பு
          </Link>
        </div>

        <PageHeader title={title} subtitle="படத்தில் ஒரு பகுதியைத் தட்டிப் பாருங்கள்" />

        {loading && (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        )}

        {!loading && error && (
          <div className="space-y-4">
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800" role="alert">
              {error}
            </p>
            <Link
              to={ROUTES.STUDENT_PICTURE_TALK}
              className="inline-flex items-center rounded-lg border-2 border-primary-600 px-4 py-2 text-base font-medium text-primary-600 hover:bg-primary-50"
            >
              Back to scenes
            </Link>
          </div>
        )}

        {!loading && !error && scene && (
          <>
            <section
              className="mb-6 rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm"
              aria-label="Picture Talk live tutor"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
                  {phaseLabel(phase)}
                </span>
                {/* Dialect selector */}
                <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-600">School dialect</label>
                    <select
                      value={dialectKey}
                      onChange={(e) => setDialectKey(e.target.value)}
                      disabled={phase === 'connecting' || phase === 'connected'}
                      title={
                        phase === 'connecting' || phase === 'connected'
                          ? 'Dialect is fixed for this call — end call to change'
                          : dialectOptions.length === 0
                            ? 'Loading school dialects…'
                            : 'Select school dialect'
                      }
                      className="text-xs px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 outline-none disabled:opacity-50"
                    >
                      {dialectOptions.length === 0 ? (
                        <option value={dialectKey}>{dialectKey} (loading school dialects…)</option>
                      ) : (
                        dialectOptions.map((d) => (
                          <option key={d.key} value={d.key}>
                            {d.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {/* <p className="text-[11px] text-slate-500 max-w-md">
                    Change dialect before starting. End call and start again to apply a different dialect.
                  </p> */}
                </div>
                {showStart && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={!scene}
                    onClick={() => {
                      if (scene) void startConversation(scene, selected ?? undefined);
                    }}
                  >
                    Start Conversation
                  </Button>
                )}
                {showEnd && (
                  <Button type="button" variant="danger" size="sm" onClick={() => void endConversation()}>
                    End Call
                  </Button>
                )}
                {phase === 'error' && (
                  <Button type="button" variant="outline" size="sm" onClick={() => void resetToIdle()}>
                    Retry
                  </Button>
                )}
              </div>
              {phase !== 'connected' && selected ? (
                <p className="mt-2 text-sm text-slate-600">
                  Start Conversation to speak with the tutor about your selection.
                </p>
              ) : null}
              {liveError ? (
                <p className="mt-2 text-sm text-red-700" role="alert">
                  {liveError}
                </p>
              ) : null}
              {/* Debug / transcript strip (lastSent, lastReply, lastModelText) — hidden
              {lastSent && (
                <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-700">
                  {lastSent ? (
                    <p>
                      <span className="font-semibold text-slate-500">Sent: </span>
                      <span className="font-mono">{lastSent}</span>
                    </p>
                  ) : null}
                  {lastReply ? (
                    <p>
                      <span className="font-semibold text-slate-500">Tutor (audio transcript): </span>
                      {lastReply}
                    </p>
                  ) : null}
                  {lastModelText ? (
                    <p>
                      <span className="font-semibold text-slate-500">Model text: </span>
                      <span className="whitespace-pre-wrap">{lastModelText}</span>
                    </p>
                  ) : null}
                </div>
              )}
              */}
            </section>

            <div className="flex flex-col gap-6 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                {imageUrl ? (
                  <PictureTalkSceneViewer
                    imageUrl={imageUrl}
                    imageWidth={scene.width}
                    imageHeight={scene.height}
                    items={scene.items}
                    selectedItemId={selected?.id ?? null}
                    onItemSelect={handleItemSelect}
                    disabled={phase === 'connecting'}
                    onHeroPrimaryLoadFailed={handleHeroPrimaryLoadFailed}
                  />
                ) : (
                  <p className="text-slate-600">No image available for this scene.</p>
                )}
              </div>

              {/* {selected ? (
                <aside className="w-full shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:max-w-sm md:sticky md:top-4">
                  <h2 className="text-xl font-bold text-slate-900">{panelTitle}</h2>
                  {panelThumb ? (
                    <div className="mt-3 overflow-hidden rounded-lg border bg-slate-50">
                      <ImageWithFallback
                        src={panelThumb}
                        alt=""
                        className="mx-auto max-h-40 w-auto object-contain"
                      />
                    </div>
                  ) : null}
                  {panelBody ? (
                    <p className="mt-3 whitespace-pre-wrap text-slate-700">{panelBody}</p>
                  ) : (
                    <p className="mt-3 text-slate-500 italic">விளக்கம் இல்லை.</p>
                  )}
                </aside>
              ) : null} */}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PictureTalkScenePage;
